import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Download } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { fmtMoney } from '../lib/quoteOptions';
import { INVOICE_VIEW_PAGE } from '../constants/testIds';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { dateStyle: 'medium' }) : '—');

export default function InvoiceView() {
  const { invoiceNumber } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [invoice, setInvoice] = useState(null);
  const [error, setError] = useState(false);

  useDocumentMeta(`Invoice ${invoiceNumber} | BulkUsedTires.com`, 'View your wholesale tire order invoice and payment status.');

  useEffect(() => {
    axios
      .get(`${API}/invoices/view/${invoiceNumber}/${token}`)
      .then((res) => setInvoice(res.data))
      .catch(() => setError(true));
  }, [invoiceNumber, token]);

  if (error) {
    return (
      <div className="min-h-screen bg-brand-bg">
        <Navbar />
        <div className="pt-40 pb-32 text-center" data-testid={INVOICE_VIEW_PAGE.notFound}>
          <p className="font-mono text-brand-muted text-sm uppercase tracking-wider">Invoice not found or link expired.</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="min-h-screen bg-brand-bg">
        <Navbar />
        <div className="pt-40 pb-32 text-center">
          <p className="font-mono text-brand-muted text-sm uppercase tracking-wider">Loading…</p>
        </div>
      </div>
    );
  }

  const statusColor = invoice.status === 'Paid' ? 'text-brand-green' : invoice.status === 'Cancelled' ? 'text-red-400' : 'text-brand-amber';

  return (
    <div className="min-h-screen bg-brand-bg">
      <Navbar />
      <section data-testid={INVOICE_VIEW_PAGE.section} className="pt-32 sm:pt-40 pb-24 sm:pb-32">
        <div className="max-w-2xl mx-auto px-5 sm:px-8">
          <p className="font-mono text-xs uppercase tracking-widest text-brand-amber mb-3">Invoice</p>
          <h1 className="font-display font-extrabold uppercase text-3xl sm:text-4xl text-brand-text">{invoice.invoice_number}</h1>
          <p className="mt-2 text-brand-muted text-sm">
            Status: <span className={`uppercase ${statusColor}`}>{invoice.status}</span> · Linked Quote {invoice.quote_number}
          </p>

          <div className="mt-8 rounded-2xl border border-brand-border bg-brand-surface p-6 sm:p-8 space-y-6">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-wider text-brand-muted mb-1">Bill To</p>
              <p className="text-brand-text text-sm">{invoice.buyer_name}</p>
              <p className="text-brand-muted text-sm">{invoice.email}</p>
            </div>
            <div className="flex justify-between items-center rounded-xl border border-brand-border px-4 py-3">
              <span className="text-brand-text font-display font-bold uppercase text-sm">Total Due</span>
              <span className="text-brand-orange font-mono text-xl font-bold">{fmtMoney(invoice.amount, invoice.currency)}</span>
            </div>
            <div>
              <p className="font-mono text-[11px] uppercase tracking-wider text-brand-muted mb-1">Payment Terms</p>
              <p className="text-brand-muted text-sm">{invoice.payment_terms || 'As agreed in your quotation.'}</p>
            </div>
            {invoice.fulfillment_status && (
              <div>
                <p className="font-mono text-[11px] uppercase tracking-wider text-brand-muted mb-1">Fulfillment</p>
                <p className="text-brand-text text-sm">{invoice.fulfillment_status}{invoice.tracking_info ? ` — ${invoice.tracking_info}` : ''}</p>
              </div>
            )}
          </div>

          <a
            href={`${API}/invoices/view/${invoiceNumber}/${token}/pdf`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={INVOICE_VIEW_PAGE.downloadPdfButton}
            className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-brand-orange hover:bg-brand-orangeHover text-white font-display font-bold uppercase tracking-wide rounded-full px-7 py-3.5 transition-colors"
          >
            <Download className="w-4 h-4" /> Download Invoice PDF
          </a>
          <p className="mt-4 text-center text-brand-muted text-xs">Issued {fmtDate(invoice.created_at)}</p>
        </div>
      </section>
      <Footer />
    </div>
  );
}
